"use client";

import { useCallback, useState } from "react";
import {
  type ChatProfileRequest,
  type ClearRagRequest,
  type LoadKnowledgeRequest,
  ChatProfileResponse,
} from "@/generated/fastapi";
import { chatProfile } from "../api/chatProfile";
import { clearKnowledge } from "../api/clearKnowledge";
import { loadKnowledge } from "../api/loadKnowledge";
import { useAuth, useRag, useUserChoices } from "@/state/globalStore";
import { showErrorToast } from "@/shared/feedback/toast";
import {
  buildSelectedProfile,
  countSelectedProfileThoughts,
} from "@/shared/utils/selectedProfile";
import type { ChatBotMessage } from "../types";

export default function useChatBot() {
  const { accessToken } = useAuth();
  const {
    selectedSituations,
    selectedThoughts,
    selectedThoughtsFree,
    isSelectedProfile,
    saveSelectedProfileSelectionSnapshot,
    hasSelectedProfileSelectionChanged,
    setInterimState,
  } = useUserChoices();
  const {
    chatMessages,
    chatSessionId,
    addChatMessage,
    updateChatMessage,
    setChatSessionId,
    resetChat,
    chatMode,
    setChatMode,
    waitingForBot,
    setWaitingForBot,
    loadingKnowledge,
    setLoadingKnowledge,
    clearingRAG,
    setClearingRAG,
    preparingChat,
    setPreparingChat,
  } = useRag();
  // Local state for use input (to debounce and avoid lag)
  const [inputQuery, setInputQuery] = useState("");

  // UI States
  const [showScrollButton, setShowScrollButton] = useState(false);

  const handleSendMessage = useCallback(
    async ({
      text,
      retryId,
      target_id,
    }: {
      text: string;
      retryId?: number;
      target_id?: string;
    }) => {
      if (!text.trim()) return;

      // Handle slash commands
      if (text.startsWith("/")) {
        // Help command
        if (text === "/help") {
          const helpLine1 = "Available slash commands: /help, /reset";
          const helpLine2 = "Use Ctrl+Enter to send messages.";

          const sysMsg: ChatBotMessage = {
            id: Date.now(),
            sender: "system",
            text: `${helpLine1}\n${helpLine2}`,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          };
          addChatMessage(sysMsg); // Use addMessage action directly
          setInputQuery("");
          return;
        }
        // Reset command
        if (text === "/reset") {
          resetChat(); // Use resetChat action from global store
          setInputQuery(""); //! Clear input locally as well
          return;
        }
      }

      const timestamp = new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });

      let userMsg: ChatBotMessage;
      if (retryId) {
        // Retry updates existing message
        updateChatMessage(retryId, { status: "pending" });
        userMsg = chatMessages.find((m) => m.id === retryId)!;
      } else {
        userMsg = {
          id: Date.now(),
          sender: "user",
          text,
          timestamp,
          status: "pending",
          reactions: [],
        };
        addChatMessage(userMsg); // Direct action instead of setMessages
        setInputQuery("");
      }

      setWaitingForBot(true);

      try {
        const request: ChatProfileRequest = {
          message: text,
          chatbot_mode: chatMode ? chatMode : undefined, // pass only if set (backend defaults to general)
          session_id: chatSessionId,
          target_id: target_id ? target_id : undefined,
        };

        const ragResponse: ChatProfileResponse | null | undefined =
          await chatProfile(accessToken, request);

        if (ragResponse) {
          if (ragResponse?.status !== "success") {
            updateChatMessage(userMsg.id, { status: "error" }); // Direct action
            return;
          }

          if (!("reply" in ragResponse)) {
            updateChatMessage(userMsg.id, { status: "error" }); // Direct action
            return;
          }

          // Update session ID if provided
          if (ragResponse.session_id) {
            setChatSessionId(ragResponse.session_id);
          }

          // Update user message status
          updateChatMessage(userMsg.id, { status: "seen" }); // Direct action

          // Add bot message
          const botMsg: ChatBotMessage = {
            id: Date.now() + 1,
            sender: "bot",
            text: ragResponse.reply || "No response received",
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            reactions: [],
          };

          addChatMessage(botMsg); // Direct action instead of setMessages

          // Process metadata if available
          if (ragResponse.metadata) {
          }
        } else {
          updateChatMessage(userMsg.id, { status: "error" }); // Direct action

          const errorMsg: ChatBotMessage = {
            id: Date.now() + 1,
            sender: "system",
            text: `Warning: No response from server`,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          };
          addChatMessage(errorMsg); // Direct action
          showErrorToast(`Something failed: No response from server`);

          return;
        }
      } catch (err) {
        updateChatMessage(userMsg.id, { status: "error" }); // Direct action

        if (err instanceof Error) {
          const errorMsg: ChatBotMessage = {
            id: Date.now() + 1,
            sender: "system",
            text: `Error: ${err.message}`,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          };
          addChatMessage(errorMsg); // Direct action
          showErrorToast(`Something failed: ${err.message}`);
        }
      } finally {
        setWaitingForBot(false);
      }
    },
    [
      accessToken,
      addChatMessage,
      chatMessages,
      chatMode,
      chatSessionId,
      resetChat,
      setInputQuery,
      setWaitingForBot,
      setChatSessionId,
      updateChatMessage,
    ],
  );

  const handleLoadKnowledgeClick = useCallback(async () => {
    if (!isSelectedProfile) {
      setChatMode("general");
      saveSelectedProfileSelectionSnapshot();
      return true;
    }

    setLoadingKnowledge(true);

    try {
      const selectedProfile = buildSelectedProfile({
        selectedSituations,
        selectedThoughts,
        selectedThoughtsFree,
        includeFreeListOrder: true,
      });
      const selectedThoughtCount = countSelectedProfileThoughts(selectedProfile);

      const request: LoadKnowledgeRequest = {
        selected_profile: selectedProfile,
        load_knowledge: true,
      };

      const result = await loadKnowledge(accessToken, request);

      if (!result) {
        const sysMsg: ChatBotMessage = {
          id: Date.now(),
          sender: "system",
          text: "Warning: No response from server",
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        addChatMessage(sysMsg);
        showErrorToast("Something failed: No response from server");
        return false;
      }

      if (result.status !== "success") {
        const sysMsgError: ChatBotMessage = {
          id: Date.now(),
          sender: "system",
          text: `Warning: ${result.message}`,
          timestamp: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        addChatMessage(sysMsgError);
        showErrorToast(`Something failed: ${result.message}`);
        return false;
      }

      const knowledgeStatusMessage =
        result.vector_store_report && result.loading_time != null
          ? `Knowledge synced for ${selectedThoughtCount} thought${selectedThoughtCount === 1 ? "" : "s"} in ${result.loading_time.toFixed(2)} seconds. ${result.vector_store_report.failed > 0 ? `${result.vector_store_report.failed} knowledge entries failed.` : result.vector_store_report.skipped > 0 ? `${result.vector_store_report.skipped} knowledge entries were already present.` : ""}`.trim()
          : result.message;

      addChatMessage({
        id: Date.now(),
        sender: "system",
        text: knowledgeStatusMessage,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      });

      const nextChatMode = result.chatbot_mode ?? "general";
      setChatMode(nextChatMode);
      addChatMessage({
        id: Date.now() + 1,
        sender: "system",
        text:
          nextChatMode === "profiler"
            ? "Chatbot is now in PROFILER mode."
            : "Chatbot is now in GENERAL mode.",
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      });

      saveSelectedProfileSelectionSnapshot();
      return true;
    } finally {
      setLoadingKnowledge(false);
    }
  }, [
    accessToken,
    addChatMessage,
    isSelectedProfile,
    saveSelectedProfileSelectionSnapshot,
    selectedSituations,
    selectedThoughts,
    selectedThoughtsFree,
    setChatMode,
    setLoadingKnowledge,
  ]);

  const clearKnowledgeForSelectionSync = useCallback(async () => {
    const request: ClearRagRequest = {
      clear_knowledge: true,
    };

    setClearingRAG(true);
    try {
      const result = await clearKnowledge(accessToken, request);

      if (!result || result.status !== "success") {
        const message = result?.message ?? "No response from server";
        showErrorToast(`Something failed: ${message}`);
        return false;
      }

      setChatMode(null);
      return true;
    } finally {
      setClearingRAG(false);
    }
  }, [accessToken, setChatMode, setClearingRAG]);

  const handleClearKnowledgeClick = useCallback(async () => {
    const request: ClearRagRequest = {
      clear_knowledge: true,
    };

    setClearingRAG(true);

    const result = await clearKnowledge(accessToken, request);

    if (!result) {
      console.error("No response received from the server");
      showErrorToast(`Something failed: No response from server`);
      setClearingRAG(false);
      return;
    }

    // Report failure if any
    if (result.status !== "success") {
      const sysMsg: ChatBotMessage = {
        id: Date.now() + 1,
        sender: "system",
        text: `Warning: ${result.status}`,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      addChatMessage(sysMsg); // Direct action
      showErrorToast(`Something failed: ${result.status}`);
    }

    // Show system message about cleared knowledge
    if (result && result.status === "success") {
      const sysMsg: ChatBotMessage = {
        id: Date.now(),
        sender: "system",
        text: `Knowledge cleared from session.`,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      setChatMode(null);
      addChatMessage(sysMsg); // Direct action
      // Reset the stored profile selection as we've cleared knowledge
      saveSelectedProfileSelectionSnapshot();
    }

    setClearingRAG(false);
  }, [
    accessToken,
    addChatMessage,
    saveSelectedProfileSelectionSnapshot,
    setChatMode,
    setClearingRAG,
  ]);

  const handleChatWithProfileClick = useCallback(
    async (loadTypingAreaWith?: string) => {
      // Finetuned - seamless UX for chat preparation. Order matters

      // Prevent double/rage clicks
      if (loadingKnowledge || preparingChat) return;

      //  Open chat interface IMMEDIATELY for responsive UX
      setInterimState("open_chat", {
        isOpen: true,
        value: loadTypingAreaWith,
      });

      // Set combined loading state for entire preparation process
      setPreparingChat(true);

      try {
        if (hasSelectedProfileSelectionChanged()) {
          const clearOk = await clearKnowledgeForSelectionSync();
          if (!clearOk) {
            return;
          }

          if (isSelectedProfile) {
            await handleLoadKnowledgeClick();
          } else {
            setChatMode("general");
            saveSelectedProfileSelectionSnapshot();
          }
        }
      } finally {
        // Always clear the preparing state
        setPreparingChat(false);
      }
    },
    [
      clearKnowledgeForSelectionSync,
      hasSelectedProfileSelectionChanged,
      handleLoadKnowledgeClick,
      isSelectedProfile,
      loadingKnowledge,
      preparingChat,
      saveSelectedProfileSelectionSnapshot,
      setInterimState,
      setChatMode,
      setPreparingChat,
    ],
  );

  const sendMessageKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && e.ctrlKey) {
        e.preventDefault();
        handleSendMessage({ text: inputQuery });
      }
    },
    [handleSendMessage, inputQuery],
  );

  // Share message (placeholder)
  const shareMessage = useCallback((text: string) => {
    alert("Shared message: " + text);
  }, []);

  // Sanitization schema to prevent XSS attacks in rendered Markdown
  const sanitizeSchema = {
    // Allow specific tags, attributes and their values
    allowedTags: [
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "p",
      "a",
      "ul",
      "ol",
      "li",
      "strong",
      "em",
      "blockquote",
      "code",
      "pre",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "hr",
      "br",
      "img",
    ],
    allowedAttributes: {
      a: ["href", "title", "target", "rel"],
      img: ["src", "alt", "title", "width", "height"],
      code: ["className"],
      pre: ["className"],
    },
    // Force all URLs to be http/https
    transformTags: {
      a: (tagName: string, attribs: { href: string }) => {
        if (attribs.href && !attribs.href.startsWith("http")) {
          return {
            tagName,
            attribs: {
              ...attribs,
              href: attribs.href.startsWith("/")
                ? attribs.href
                : `/${attribs.href}`,
              target: "_blank",
              rel: "noopener noreferrer",
            },
          };
        }
        return {
          tagName,
          attribs: {
            ...attribs,
            target: "_blank",
            rel: "noopener noreferrer",
          },
        };
      },
    },
  };

  return {
    waitingForBot,
    loadingKnowledge,
    clearingRAG,
    preparingChat,
    showScrollButton,
    setShowScrollButton,
    inputQuery,
    setInputQuery,
    sendMessageKeyDown,
    handleSendMessage,
    handleLoadKnowledgeClick,
    handleClearKnowledgeClick,
    handleChatWithProfileClick,
    shareMessage,
    sanitizeSchema,
  };
}
